import DOMPurify from 'dompurify'

const purifier = DOMPurify(window)
const FORBIDDEN_SELECTOR = 'button,embed,form,iframe,input,object,script,select,style'
const URI_ATTRIBUTES = new Set(['action', 'formaction', 'href', 'poster', 'src', 'xlink:href'])

interface BaselineResult {
  html: string
  removedUnsafeContent: boolean
}

function enforceBaseline(html: string): BaselineResult {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  let removedUnsafeContent = false

  for (const element of parsed.body.querySelectorAll(FORBIDDEN_SELECTOR)) {
    element.remove()
    removedUnsafeContent = true
  }

  for (const element of parsed.body.querySelectorAll('*')) {
    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase()
      const compactValue = [...attribute.value]
        .filter((character) => (character.codePointAt(0) ?? 0) > 0x20)
        .join('')
        .toLowerCase()
      const unsafeAttribute =
        name.startsWith('on') ||
        name === 'style' ||
        (URI_ATTRIBUTES.has(name) && compactValue.startsWith('javascript:'))
      if (!unsafeAttribute) continue
      element.removeAttribute(attribute.name)
      removedUnsafeContent = true
    }
  }

  return { html: parsed.body.innerHTML, removedUnsafeContent }
}

/** The one HTML-sanitisation boundary for shared UI renderers. */
export function sanitizeHtml(untrustedHtml: string): string {
  if (!purifier.isSupported) return ''
  const purified = purifier.sanitize(untrustedHtml, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['button', 'embed', 'form', 'iframe', 'input', 'object', 'select', 'style'],
    FORBID_ATTR: ['style'],
  })

  const checked = enforceBaseline(purified)
  if (!checked.removedUnsafeContent) return checked.html

  // happy-dom does not implement every browser API DOMPurify relies on. Keep the unit-test DOM
  // deterministic with the strict baseline; real browsers fail closed if DOMPurify ever leaves
  // a forbidden node or attribute behind.
  return 'happyDOM' in window ? enforceBaseline(untrustedHtml).html : ''
}
